import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { openInNewTab } from '../../utils/openNewTab';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faUser,
  faHashtag,
  faEnvelope,
  faPhone,
  faBuilding,
  faMapMarkerAlt,
  faCity,
  faMap,
  faMailBulk,
  faFlag,
  faBirthdayCake,
  faCalendarPlus,
  faCalendar,
  faInfoCircle,
  faBriefcase,
  faFileAlt,
  faExternalLinkAlt,
  faPlus,
  faSave,
  faCheck,
  faExclamationTriangle,
  faSpinner,
  faUserTimes,
  faLock,
  faTrash,
  faPaperPlane,
} from '@fortawesome/free-solid-svg-icons';
import {
  useContact,
  useUpdateContact,
  useContactFieldDefs,
  useDeleteContact,
  usePortalInviteStatus,
  useSendPortalInvite,
  useResendPortalInvite,
} from '../../hooks/useContact';
import { useToast } from '../../hooks/useToast';
import { usePermissions } from '../../hooks/usePermissions';
import { Card, CardHeader, CardBody } from '../../components/ui/Card/Card';
import type { AssociatedCase, FieldMapping } from '../../types/contact';
import styles from './ContactPage.module.css';
import EmptyState from '../../components/ui/EmptyState/EmptyState';
import DatePicker from '../../components/ui/DatePicker/DatePicker';

/* ── Type definitions ── */
type ApiErrorResponse = {
  response?: {
    data?: {
      error?: string;
    };
  };
};

function getApiErrorMessage(error: unknown, fallback: string): string {
  const maybeError = error as ApiErrorResponse;
  return maybeError.response?.data?.error || fallback;
}

const ICON_LOOKUP: Record<string, typeof faUser> = {
  hashtag: faHashtag,
  user: faUser,
  envelope: faEnvelope,
  phone: faPhone,
  building: faBuilding,
  'map-marker': faMapMarkerAlt,
  city: faCity,
  map: faMap,
  'mail-bulk': faMailBulk,
  flag: faFlag,
  'birthday-cake': faBirthdayCake,
  'calendar-plus': faCalendarPlus,
  calendar: faCalendar,
  'info-circle': faInfoCircle,
};

const HIDDEN_FIELDS = new Set(['password', 'Password']);

/* ────────────────────────────────────────────────────────── */
/*  Helpers                                                   */
/* ────────────────────────────────────────────────────────── */

/** Format ISO date → YYYY-MM-DD for <input type="date"> */
function toInputDate(raw: string): string {
  if (!raw) return '';
  const str = String(raw);
  if (/^\d{4}-\d{2}-\d{2}$/.test(str)) return str;
  // Use UTC methods — timezone-neutral since backend stores UTC midnight
  const d = new Date(str);
  if (isNaN(d.getTime())) return '';
  const y = d.getUTCFullYear();
  const m = String(d.getUTCMonth() + 1).padStart(2, '0');
  const day = String(d.getUTCDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

/** Format ISO date → readable display string */
function formatDate(raw: string): string {
  try {
    const d = new Date(raw);
    if (isNaN(d.getTime())) return raw;
    return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
  } catch {
    return raw;
  }
}

/** Format ISO datetime → readable display string */
function formatDateTime(raw: string): string {
  try {
    const d = new Date(raw);
    if (isNaN(d.getTime())) return raw;
    return d.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return raw;
  }
}

/** Best-effort detect ISO-ish date string */
function looksLikeDate(val: unknown): boolean {
  return typeof val === 'string' && /\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/.test(val);
}

/* ────────────────────────────────────────────────────────── */
/*  Sub-components                                            */
/* ────────────────────────────────────────────────────────── */

interface ContactFieldProps {
  fieldKey: string;
  value: string;
  mapping: FieldMapping;
  onChange: (key: string, val: string) => void;
  forceReadOnly?: boolean;
}

function ContactField({ fieldKey, value, mapping, onChange, forceReadOnly }: ContactFieldProps) {
  const icon = ICON_LOOKUP[mapping.icon] ?? faInfoCircle;
  const isEmpty = !value || value.trim() === '';

  if (mapping.editable && !forceReadOnly) {
    const displayVal = mapping.type === 'date' && value ? toInputDate(value) : value;

    if (mapping.type === 'date') {
      return (
        <div className={styles.field}>
          <label className={styles.fieldLabel}>
            <FontAwesomeIcon icon={icon} />
            {mapping.label}
          </label>
          <DatePicker
            value={displayVal}
            onChange={(v) => onChange(fieldKey, v)}
            className={styles.fieldInput}
          />
        </div>
      );
    }

    return (
      <div className={styles.field}>
        <label className={styles.fieldLabel}>
          <FontAwesomeIcon icon={icon} />
          {mapping.label}
        </label>
        <input
          type="text"
          className={styles.fieldInput}
          value={displayVal}
          placeholder={isEmpty ? 'Not provided' : ''}
          onChange={(e) => onChange(fieldKey, e.target.value)}
        />
      </div>
    );
  }

  // Read-only field
  let display = value;
  if (mapping.type === 'date' && value) display = formatDate(value);
  if (mapping.type === 'datetime' && value) display = formatDateTime(value);

  return (
    <div className={styles.field}>
      <span className={styles.fieldLabel}>
        <FontAwesomeIcon icon={icon} />
        {mapping.label}
      </span>
      <div className={`${styles.fieldValue} ${isEmpty ? styles.fieldValueEmpty : ''}`}>
        {isEmpty ? 'Not provided' : display}
      </div>
    </div>
  );
}

interface CaseCardProps {
  caseData: AssociatedCase;
  blocked?: boolean;
}

function CaseCard({ caseData, blocked = false }: CaseCardProps) {
  const caseRef = caseData.case_ref || caseData.CaseRef || 'Unknown';
  const defendant = caseData.defendant_name || caseData.defendant || caseData.Defendant || '';
  const caseType = caseData.case_type || caseData.CaseType || 'Unknown';
  const stage = caseData.stage || caseData.Stage || 'Unknown';
  const solicitor =
    caseData.solicitor || caseData.Solicitor || caseData.handler || caseData.owner || 'Unassigned';
  const caseTitle = defendant ? `${caseRef}-${defendant}` : caseRef;
  const navigate = useNavigate();

  return (
    <div
      className={`${styles.caseCard} ${blocked ? styles.caseCardBlocked : ''}`}
      title={blocked ? 'You do not have permission to access this case type' : undefined}
    >
      {blocked && (
        <div className={styles.caseBlockedBadge}>
          <FontAwesomeIcon icon={faLock} />
          No access
        </div>
      )}
      <h3 className={styles.caseTitle}>
        <FontAwesomeIcon icon={faFileAlt} />
        {caseTitle}
      </h3>
      <div className={styles.caseDetails}>
        <div className={styles.caseDetail}>
          <span className={styles.caseDetailLabel}>Case Type</span>
          <span className={styles.caseDetailValue}>{caseType}</span>
        </div>
        <div className={styles.caseDetail}>
          <span className={styles.caseDetailLabel}>Stage</span>
          <span className={styles.caseDetailValue}>{blocked ? '—' : stage}</span>
        </div>
        <div className={styles.caseDetail}>
          <span className={styles.caseDetailLabel}>Solicitor</span>
          <span className={styles.caseDetailValue}>{blocked ? '—' : solicitor}</span>
        </div>
      </div>
      {!blocked && (
        <div className={styles.caseActions}>
          <button
            type="button"
            className={styles.openCaseBtn}
            onClick={() =>
              navigate(
                `/cases/${encodeURIComponent(caseRef)}${caseData.case_type_id ? `?caseTypeId=${caseData.case_type_id}` : ''}`,
              )
            }
          >
            Open
          </button>
          <button
            type="button"
            className={styles.openCaseBtnSecondary}
            onClick={() =>
              openInNewTab(
                `/cases/${encodeURIComponent(caseRef)}${caseData.case_type_id ? `?caseTypeId=${caseData.case_type_id}` : ''}`,
              )
            }
          >
            <FontAwesomeIcon icon={faExternalLinkAlt} />
            Open in new tab
          </button>
        </div>
      )}
    </div>
  );
}

/* ────────────────────────────────────────────────────────── */
/*  Main page component                                       */
/* ────────────────────────────────────────────────────────── */

export default function ContactPage() {
  const [searchParams] = useSearchParams();
  const { id: paramId } = useParams<{ id: string }>();
  // Support both /contacts/:id and /contacts?id=xxx
  const contactId = paramId || searchParams.get('id') || undefined;

  const navigate = useNavigate();
  const { showToast } = useToast();

  // Data fetching
  const { data, isLoading, isError, error } = useContact(contactId);
  const updateMutation = useUpdateContact(contactId);
  const deleteContactMut = useDeleteContact(contactId);
  const { data: dynamicFieldMap } = useContactFieldDefs();
  const { isSuperAdmin, hasPermission, allowedCaseTypes } = usePermissions();
  const canEditContacts = isSuperAdmin || hasPermission('edit_contacts');
  const canCreateCases = isSuperAdmin || hasPermission('create_cases');
  const canDeleteContact = isSuperAdmin || hasPermission('delete_contact');
  const canManagePortalInvites = isSuperAdmin || hasPermission('manage_portal_invitations');

  // Portal invite
  const { data: portalStatus } = usePortalInviteStatus(contactId);
  const sendInviteMut = useSendPortalInvite(contactId);
  const resendInviteMut = useResendPortalInvite(contactId);
  const [inviteSent, setInviteSent] = useState(false);

  // Delete contact state
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const handleDeleteContact = async () => {
    try {
      await deleteContactMut.mutateAsync();
      showToast('Contact deleted successfully', 'success');
      setDeleteModalOpen(false);
      navigate('/');
    } catch {
      showToast('Failed to delete contact', 'error');
    }
  };

  // Field map from DB (keyed by field_key → FieldMapping)
  const FIELD_MAP = dynamicFieldMap ?? {};

  // Local form state (tracks edits)
  const [formValues, setFormValues] = useState<Record<string, string>>({});
  const [origValues, setOrigValues] = useState<Record<string, string>>({});
  const [saveState, setSaveState] = useState<'idle' | 'saving' | 'success' | 'error'>('idle');

  // Populate form when data arrives
  useEffect(() => {
    if (data?.contact) {
      const vals: Record<string, string> = {};
      for (const [k, v] of Object.entries(data.contact)) {
        if (HIDDEN_FIELDS.has(k)) continue;
        vals[k] = v != null ? String(v) : '';
      }
      setFormValues(vals);
      setOrigValues(vals);
    }
  }, [data]);

  // Dirty check
  const isDirty = Object.keys(formValues).some((k) => formValues[k] !== origValues[k]);

  // Unsaved changes guard
  useEffect(() => {
    function handleBeforeUnload(e: BeforeUnloadEvent) {
      if (isDirty) {
        e.preventDefault();
      }
    }
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [isDirty]);

  // Field change handler
  const handleFieldChange = useCallback((key: string, val: string) => {
    setFormValues((prev) => ({ ...prev, [key]: val }));
  }, []);

  // Save handler
  const handleSave = useCallback(async () => {
    if (!contactId) return;
    setSaveState('saving');

    // Build update payload — only changed fields, convert dates back to ISO
    const updates: Record<string, string | null> = {};
    for (const [k, v] of Object.entries(formValues)) {
      if (v !== origValues[k]) {
        const mapping = FIELD_MAP[k];
        if (mapping?.type === 'date' && v) {
          // Send plain YYYY-MM-DD — appending Z shifts into UTC and saves the previous day in BST
          updates[k] = v;
        } else {
          updates[k] = v || null;
        }
      }
    }

    try {
      await updateMutation.mutateAsync(updates);
      setSaveState('success');
      setOrigValues({ ...formValues });
      showToast('Contact saved successfully', 'success');
      setTimeout(() => setSaveState('idle'), 2000);
    } catch (err) {
      setSaveState('error');
      showToast(`Failed to save: ${(err as Error).message}`, 'error');
      setTimeout(() => setSaveState('idle'), 3000);
    }
  }, [contactId, formValues, origValues, updateMutation, showToast]);

  // ── No contact ID ──
  if (!contactId) {
    return (
      <div className={styles.error}>
        <FontAwesomeIcon icon={faExclamationTriangle} />
        <h3>No Contact ID</h3>
        <p>No contact ID was provided in the URL.</p>
      </div>
    );
  }

  // ── Loading ──
  if (isLoading) {
    return (
      <div className={styles.loading}>
        <FontAwesomeIcon icon={faSpinner} spin />
        <span>Loading contact information…</span>
      </div>
    );
  }

  // ── Error ──
  if (isError) {
    return (
      <div className={styles.error}>
        <FontAwesomeIcon icon={faExclamationTriangle} />
        <h3>Error Loading Contact</h3>
        <p>{(error as Error)?.message || 'An unexpected error occurred.'}</p>
      </div>
    );
  }

  const contact = data?.contact;
  const cases = data?.cases ?? [];

  if (!contact) {
    return (
      <EmptyState
        icon={<FontAwesomeIcon icon={faUserTimes} />}
        title="Contact Not Found"
        message="The requested contact could not be found."
      />
    );
  }

  // Build field entries for rendering (skip hidden/password)
  const fieldEntries = Object.entries(formValues).filter(([k]) => !HIDDEN_FIELDS.has(k));

  // Save button label & class
  let saveBtnClass = styles.saveBtn;
  let saveBtnIcon = faSave;
  let saveBtnLabel = 'Save Changes';
  if (isDirty) saveBtnClass += ` ${styles.saveBtnDirty}`;
  if (saveState === 'saving') {
    saveBtnIcon = faSpinner;
    saveBtnLabel = 'Saving…';
  }
  if (saveState === 'success') {
    saveBtnClass += ` ${styles.saveBtnSuccess}`;
    saveBtnIcon = faCheck;
    saveBtnLabel = 'Saved!';
  }
  if (saveState === 'error') {
    saveBtnClass += ` ${styles.saveBtnError}`;
    saveBtnIcon = faExclamationTriangle;
    saveBtnLabel = 'Error';
  }

  return (
    <>
      {/* Action bar */}
      <div className={styles.actionBar}>
        <button
          className={saveBtnClass}
          onClick={handleSave}
          disabled={!isDirty || saveState === 'saving'}
          type="button"
        >
          <FontAwesomeIcon icon={saveBtnIcon} spin={saveState === 'saving'} />
          {saveBtnLabel}
        </button>
        {canDeleteContact && (
          <button
            className={styles.deleteBtn}
            onClick={() => setDeleteModalOpen(true)}
            type="button"
          >
            <FontAwesomeIcon icon={faTrash} />
            Delete Contact
          </button>
        )}
      </div>

      {/* Delete Contact Confirmation Modal */}
      {deleteModalOpen && (
        <div className={styles.modalOverlay} onClick={() => setDeleteModalOpen(false)}>
          <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <h4 className={styles.modalTitle}>Delete Contact</h4>
            </div>
            <div className={styles.modalBody}>
              <p style={{ color: 'var(--danger-600, #dc2626)', fontWeight: 600 }}>
                Are you sure you want to permanently delete this contact?
              </p>
              <p>
                This will remove the contact and all associated cases and their related records.
                This action cannot be undone.
              </p>
            </div>
            <div className={styles.modalFooter}>
              <button onClick={() => setDeleteModalOpen(false)}>Cancel</button>
              <button
                onClick={handleDeleteContact}
                disabled={deleteContactMut.isPending}
                style={{ backgroundColor: 'var(--danger-600, #dc2626)', color: '#fff' }}
              >
                {deleteContactMut.isPending ? 'Deleting…' : 'Delete Contact'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Portal Invite */}
      {canManagePortalInvites && (
        <div style={{ marginBottom: 16 }}>
          <Card className={styles.contactCard}>
            <CardBody>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  flexWrap: 'wrap',
                  gap: 12,
                }}
              >
                <div>
                  <h3 style={{ margin: '0 0 4px', fontSize: '0.95rem' }}>
                    <FontAwesomeIcon icon={faEnvelope} style={{ color: 'var(--primary-600)' }} />{' '}
                    Client Portal Access
                  </h3>
                  {portalStatus?.has_portal_account ? (
                    <span style={{ fontSize: '0.85rem', color: 'var(--success-600, #16a34a)' }}>
                      ✓ Portal account active
                      {portalStatus.portal_user?.last_login_at
                        ? ` — last login ${new Date(portalStatus.portal_user.last_login_at).toLocaleDateString('en-GB')}`
                        : ' — never logged in'}
                    </span>
                  ) : portalStatus?.invitations?.some((i) => i.status === 'accepted') ? (
                    <span style={{ fontSize: '0.85rem', color: 'var(--success-600, #16a34a)' }}>
                      ✓ Account created — awaiting first login
                    </span>
                  ) : (
                    <span style={{ fontSize: '0.85rem', color: 'var(--text-secondary)' }}>
                      No portal access set up
                    </span>
                  )}
                </div>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  {!portalStatus?.has_portal_account &&
                    !portalStatus?.invitations?.some((i) => i.status === 'accepted') && (
                      <button
                        type="button"
                        style={{
                          padding: '8px 18px',
                          background: 'var(--primary-600, #2563eb)',
                          color: '#fff',
                          border: 'none',
                          borderRadius: 6,
                          cursor: 'pointer',
                          fontWeight: 600,
                          fontSize: '0.85rem',
                          opacity: sendInviteMut.isPending || inviteSent ? 0.5 : 1,
                        }}
                        disabled={sendInviteMut.isPending || inviteSent}
                        onClick={async () => {
                          try {
                            await sendInviteMut.mutateAsync();
                            setInviteSent(true);
                            showToast('Portal account created & invite sent', 'success');
                          } catch (err: unknown) {
                            showToast(getApiErrorMessage(err, 'Failed to send invite'), 'error');
                          }
                        }}
                      >
                        <FontAwesomeIcon icon={faPaperPlane} />{' '}
                        {sendInviteMut.isPending
                          ? 'Creating…'
                          : inviteSent
                            ? 'Invite Sent'
                            : 'Send Portal Invite'}
                      </button>
                    )}
                  {(portalStatus?.has_portal_account ||
                    portalStatus?.invitations?.some((i) => i.status === 'accepted')) && (
                    <button
                      type="button"
                      style={{
                        padding: '8px 18px',
                        background: 'var(--warning-600, #d97706)',
                        color: '#fff',
                        border: 'none',
                        borderRadius: 6,
                        cursor: 'pointer',
                        fontWeight: 600,
                        fontSize: '0.85rem',
                        opacity: resendInviteMut.isPending ? 0.5 : 1,
                      }}
                      disabled={resendInviteMut.isPending}
                      onClick={async () => {
                        try {
                          await resendInviteMut.mutateAsync();
                          showToast('New credentials sent to client', 'success');
                        } catch (err: unknown) {
                          showToast(getApiErrorMessage(err, 'Failed to resend invite'), 'error');
                        }
                      }}
                    >
                      <FontAwesomeIcon icon={faPaperPlane} />{' '}
                      {resendInviteMut.isPending ? 'Sending…' : 'Resend Credentials'}
                    </button>
                  )}
                </div>
              </div>
            </CardBody>
          </Card>
        </div>
      )}

      {/* Contact Information */}
      <Card className={styles.contactCard}>
        <CardHeader>
          <h2>
            <FontAwesomeIcon icon={faUser} style={{ color: 'var(--primary-600)' }} /> Contact
            Information
          </h2>
        </CardHeader>
        <CardBody>
          <div className={styles.fieldsGrid}>
            {fieldEntries.map(([key, val]) => {
              let mapping = FIELD_MAP[key];
              // Auto-detect date strings without explicit mapping
              if (!mapping && looksLikeDate(val)) {
                mapping = {
                  label: key.replace(/_/g, ' '),
                  icon: 'calendar',
                  editable: false,
                  type: 'date',
                };
              }
              if (!mapping) {
                mapping = { label: key.replace(/_/g, ' '), icon: 'info-circle', editable: true };
              }
              return (
                <ContactField
                  key={key}
                  fieldKey={key}
                  value={val}
                  mapping={mapping}
                  onChange={handleFieldChange}
                  forceReadOnly={!canEditContacts}
                />
              );
            })}
          </div>
        </CardBody>
      </Card>

      {/* Associated Cases */}
      <Card className={styles.casesCard}>
        <CardHeader>
          <div className={styles.casesHeader}>
            <h2>
              <FontAwesomeIcon icon={faBriefcase} style={{ color: 'var(--primary-600)' }} />{' '}
              {cases.length} Associated Cases
            </h2>
            {canCreateCases && (
              <button
                className={styles.newCaseBtn}
                onClick={() => navigate('/new-record')}
                type="button"
              >
                <FontAwesomeIcon icon={faPlus} />
                New Case
              </button>
            )}
          </div>
        </CardHeader>
        <CardBody>
          {cases.length === 0 ? (
            <EmptyState
              icon={<FontAwesomeIcon icon={faBriefcase} />}
              title="No Associated Cases"
              message="This contact has no associated cases in the system."
            />
          ) : (
            <div className={styles.casesGrid}>
              {cases.map((c, i) => {
                const ct = (c.case_type || c.CaseType || '').toLowerCase();
                const isBlocked =
                  allowedCaseTypes !== null && allowedCaseTypes !== undefined
                    ? !allowedCaseTypes.some((p) => p.toLowerCase() === ct)
                    : false;
                return (
                  <CaseCard key={c.case_ref || c.CaseRef || i} caseData={c} blocked={isBlocked} />
                );
              })}
            </div>
          )}
        </CardBody>
      </Card>
    </>
  );
}
